import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Banknote,
  CloudUpload,
  Plus,
  Trash2,
  FileDown,
  RefreshCw,
  SquarePen,
  Gift
} from 'lucide-react';

const CashbackConfig = ({
  campaignId,
  enabled = false,
  mechanicsType = '',
  mechanicsTypeCode = '',
  onEnabledChange,
  api,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError
}) => {
  const [stats, setStats] = useState({ total: 0, available: 0, used: 0 });
  const [vouchers, setVouchers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [addingSingle, setAddingSingle] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [singleCode, setSingleCode] = useState('');
  const [singleAmount, setSingleAmount] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [editCode, setEditCode] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [gcTiers, setGcTiers] = useState([]);
  const [gcLoading, setGcLoading] = useState(false);
  const [gcSaving, setGcSaving] = useState(false);
  const [gcTierDeletingId, setGcTierDeletingId] = useState(null);
  const [gcRuleSaving, setGcRuleSaving] = useState(false);
  const [gcTierAdding, setGcTierAdding] = useState(false);
  const [gcNewAmount, setGcNewAmount] = useState('');
  const [gcNewMaxWinners, setGcNewMaxWinners] = useState('');
  const [gcWin1X, setGcWin1X] = useState('');
  const [tierToRemove, setTierToRemove] = useState(null);
  const [gcTcnProfiles, setGcTcnProfiles] = useState([]);
  const [gcSelectedTcnProfileId, setGcSelectedTcnProfileId] = useState('');
  const [gcTcnSaving, setGcTcnSaving] = useState(false);
  const [gcTcnCreateOpen, setGcTcnCreateOpen] = useState(false);
  const [gcTcnDeletingId, setGcTcnDeletingId] = useState(null);
  const [gcTcnCreateSaving, setGcTcnCreateSaving] = useState(false);
  const [gcTcnNew, setGcTcnNew] = useState({
    name: '',
    token_url: '',
    api_url: '',
    client_id: '',
    client_secret: '',
    customer_ref: '',
    promotion_id: '',
    product_id: ''
  });

  const isWin1OfXMechanic = ['win 1 of x', 'win_1_of_x'].includes(String(mechanicsType || '').trim().toLowerCase())
    || ['win 1 of x', 'win_1_of_x'].includes(String(mechanicsTypeCode || '').trim().toLowerCase());

  const fetchGiftCardSettings = useCallback(async () => {
    if (!campaignId) return;
    setGcLoading(true);
    try {
      const { data } = await api.get(`/admin/campaigns/${campaignId}/cashback/gift-card-settings`);
      setGcTiers(data.tiers || []);
      setGcWin1X(data.win1_x != null ? String(data.win1_x) : '');
      setGcTcnProfiles(Array.isArray(data.tcn_profiles) ? data.tcn_profiles : []);
      setGcSelectedTcnProfileId(
        data.selected_tcn_profile_id != null && data.selected_tcn_profile_id !== ''
          ? String(data.selected_tcn_profile_id)
          : ''
      );
    } catch (err) {
      console.error('Fetch gift card settings:', err);
    } finally {
      setGcLoading(false);
    }
  }, [campaignId, api]);

  const handleSaveTcnProfile = async (value) => {
    if (!campaignId) return;
    setGcTcnSaving(true);
    try {
      const payload = { gift_card_tcn_settings_id: value ? parseInt(value, 10) : null };
      await api.put(`/admin/campaigns/${campaignId}/cashback/tcn-profile`, payload);
      setSuccessMessage('TCN profile saved.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
      setGcSelectedTcnProfileId(value);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to save TCN profile');
      setShowError(true);
    } finally {
      setGcTcnSaving(false);
    }
  };

  const handleCreateTcnProfile = async (e) => {
    e?.preventDefault?.();
    setGcTcnCreateSaving(true);
    try {
      const payload = { ...gcTcnNew };
      const { data } = await api.post('/admin/tcn-gift-card-profiles', payload);
      setSuccessMessage(`TCN profile created: ${data?.profile?.name || payload.name}`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
      setGcTcnCreateOpen(false);
      setGcTcnNew({
        name: '',
        token_url: '',
        api_url: '',
        client_id: '',
        client_secret: '',
        customer_ref: '',
        promotion_id: '',
        product_id: ''
      });
      fetchGiftCardSettings();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to create TCN profile');
      setShowError(true);
    } finally {
      setGcTcnCreateSaving(false);
    }
  };

  const handleDeleteTcnProfile = async (profileId) => {
    if (!profileId) return;
    setGcTcnDeletingId(profileId);
    try {
      await api.delete(`/admin/tcn-gift-card-profiles/${profileId}`);
      if (String(gcSelectedTcnProfileId) === String(profileId)) {
        // If we deleted the selected one, reset to env default locally (campaign selection is detached server-side too)
        setGcSelectedTcnProfileId('');
      }
      setSuccessMessage('TCN profile deleted.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
      fetchGiftCardSettings();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to delete TCN profile');
      setShowError(true);
    } finally {
      setGcTcnDeletingId(null);
    }
  };

  useEffect(() => {
    if (!enabled) {
      fetchGiftCardSettings();
    }
  }, [enabled, fetchGiftCardSettings]);

  const handleAddTier = async () => {
    const amount = parseFloat(gcNewAmount);
    const max_winners = parseInt(gcNewMaxWinners, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      setErrorMessage('Amount must be a positive number.');
      setShowError(true);
      return;
    }
    if (!Number.isFinite(max_winners) || max_winners <= 0) {
      setErrorMessage('Max winners must be a positive integer.');
      setShowError(true);
      return;
    }
    const key = `${Number(amount).toFixed(2)}:${max_winners}`;
    const exists = gcTiers.some(t => `${Number(t.amount).toFixed(2)}:${t.max_winners}` === key);
    if (exists) {
      setErrorMessage('That tier already exists.');
      setShowError(true);
      return;
    }
    if (!campaignId) {
      setGcTiers(prev => [...prev, { amount, max_winners, issued_count: 0 }]);
      setGcNewAmount('');
      setGcNewMaxWinners('');
      return;
    }

    setGcTierAdding(true);
    try {
      await api.post(`/admin/campaigns/${campaignId}/cashback/gift-card-settings`, {
        amount,
        max_winners
      });
      setGcNewAmount('');
      setGcNewMaxWinners('');
      fetchGiftCardSettings();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to add tier');
      setShowError(true);
    } finally {
      setGcTierAdding(false);
    }
  };

  const handleRemoveTier = async (index) => {
    const tier = gcTiers[index];
    if (!tier) {
      setTierToRemove(null);
      return;
    }

    // If tier has no id yet (not saved), remove locally.
    if (!tier.id) {
      setGcTiers(prev => prev.filter((_, i) => i !== index));
      setTierToRemove(null);
      return;
    }

    setGcTierDeletingId(tier.id);
    try {
      await api.delete(`/admin/campaigns/${campaignId}/cashback/gift-card-settings/${tier.id}`);
      setSuccessMessage('Tier deleted.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
      setTierToRemove(null);
      fetchGiftCardSettings();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to delete tier');
      setShowError(true);
    } finally {
      setGcTierDeletingId(null);
    }
  };

  const handleSaveGiftCardRule = async () => {
    if (!campaignId) return;
    setGcRuleSaving(true);
    try {
      const parsedWin1X = parseInt(gcWin1X, 10);
      if (isWin1OfXMechanic && (!Number.isFinite(parsedWin1X) || parsedWin1X < 2)) {
        setErrorMessage('Win 1 of X must be 2 or greater.');
        setShowError(true);
        return;
      }
      await api.put(`/admin/campaigns/${campaignId}/cashback/gift-card-win1x`, {
        win1_x: isWin1OfXMechanic ? parsedWin1X : null
      });
      setSuccessMessage('Reward rule saved.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
      fetchGiftCardSettings();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to save reward rule');
      setShowError(true);
    } finally {
      setGcRuleSaving(false);
    }
  };

  const fetchStats = useCallback(async () => {
    if (!campaignId) return;
    try {
      const { data } = await api.get(`/admin/campaigns/${campaignId}/cashback/vouchers/stats`);
      setStats(data);
    } catch (err) {
      console.error('Fetch cashback stats:', err);
    }
  }, [campaignId, api]);

  const fetchVouchers = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('search', search.trim());
      const { data } = await api.get(`/admin/campaigns/${campaignId}/cashback/vouchers?${params}`);
      setVouchers(data.vouchers || []);
      setPagination(data.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 });
      if (data.stats) setStats(data.stats);
    } catch (err) {
      console.error('Fetch cashback vouchers:', err);
      setErrorMessage(err.response?.data?.error || 'Failed to load vouchers');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, [campaignId, api, page, statusFilter, search]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const handleUploadCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setErrorMessage('Please select a CSV file.');
      setShowError(true);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('replace_existing', replaceExisting);
      const { data } = await api.post(`/admin/campaigns/${campaignId}/cashback/vouchers/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStats(data.stats || stats);
      setCsvFile(null);
      setReplaceExisting(false);
      setSuccessMessage(`Uploaded ${data.inserted || 0} voucher(s).${data.skipped ? ` ${data.skipped} skipped (duplicates).` : ''}`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);
      fetchVouchers();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Upload failed');
      setShowError(true);
      setTimeout(() => setShowError(false), 4000);
    } finally {
      setUploading(false);
    }
  };

  const handleAddSingle = async (e) => {
    e.preventDefault();
    const code = singleCode.trim();
    if (!code) return;
    setAddingSingle(true);
    try {
      const payload = { voucher_code: code };
      if (singleAmount.trim() !== '') {
        const amt = parseFloat(singleAmount);
        if (!Number.isNaN(amt) && amt >= 0) payload.voucher_amount = amt;
      }
      const { data } = await api.post(`/admin/campaigns/${campaignId}/cashback/vouchers`, payload);
      setStats(data.stats || stats);
      setSingleCode('');
      setSingleAmount('');
      setSuccessMessage('Voucher added.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      fetchVouchers();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to add voucher');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setAddingSingle(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/campaigns/${campaignId}/cashback/vouchers`, {
        data: { voucher_ids: Array.from(selectedIds) }
      });
      setSelectedIds(new Set());
      setSuccessMessage('Selected vouchers deleted.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      fetchStats();
      fetchVouchers();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to delete');
      setShowError(true);
    } finally {
      setDeleting(false);
    }
  };

  const openEditVoucher = (v) => {
    setEditingVoucher(v);
    setEditCode(v.voucher_code || '');
    setEditAmount(v.voucher_amount != null ? String(v.voucher_amount) : '');
  };

  const closeEditVoucher = () => {
    setEditingVoucher(null);
    setEditCode('');
    setEditAmount('');
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingVoucher || !editCode.trim()) return;
    setSavingEdit(true);
    try {
      const payload = { voucher_code: editCode.trim() };
      if (editAmount.trim() !== '') {
        const amt = parseFloat(editAmount);
        if (Number.isFinite(amt) && amt >= 0) payload.voucher_amount = amt;
        else payload.voucher_amount = null;
      } else {
        payload.voucher_amount = null;
      }
      await api.put(`/admin/campaigns/${campaignId}/cashback/vouchers/${editingVoucher.id}`, payload);
      setSuccessMessage('Voucher updated.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      closeEditVoucher();
      fetchStats();
      fetchVouchers();
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to update voucher');
      setShowError(true);
    } finally {
      setSavingEdit(false);
    }
  };

  const applySearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    return isNaN(date.getTime()) ? d : date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-gray-700">
        <Banknote className="w-6 h-6 text-teal-600" />
        <h3 className="text-xl font-bold text-gray-900">Cashback Vouchers</h3>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="bg-gray-50 p-6 rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-lg font-medium text-gray-900">Cashback Voucher Status</h4>
            <p className="text-sm text-gray-600">Enable cashback vouchers for eligible entries</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={async (e) => {
                const newEnabled = e.target.checked;
                onEnabledChange(newEnabled);
                try {
                  await api.patch(`/admin/campaigns/${campaignId}/cashback`, {
                    enabled: newEnabled
                  });
                  setSuccessMessage(newEnabled ? 'Cashback vouchers enabled!' : 'Cashback vouchers disabled!');
                  setShowSuccess(true);
                  setTimeout(() => setShowSuccess(false), 3000);
                } catch (err) {
                  console.error('Failed to toggle cashback vouchers:', err);
                  setErrorMessage('Failed to save cashback voucher status');
                  setShowError(true);
                  setTimeout(() => setShowError(false), 3000);
                  onEnabledChange(!newEnabled);
                }
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-teal-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
          </label>
        </div>
      </div>

      {!enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-5 h-5 text-blue-600" />
            <h4 className="text-lg font-medium text-gray-900">TCN Gift Card Settings</h4>
          </div>
          <p className="text-sm text-gray-600 mb-5">
            When cashback vouchers are disabled, eligible entries will automatically receive a TCN gift card — up to the configured maximum number of winners.
          </p>
          {gcLoading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading settings…
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white border border-blue-100 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900">TCN Profile</h5>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose which stored TCN credentials to use for this campaign. If none is selected, the system will fall back to server <code className="font-mono">.env</code> defaults.
                    </p>
                  </div>
                  <Link
                    to="/admin/tcn-profiles"
                    className="text-xs font-medium text-blue-700 hover:text-blue-900 underline underline-offset-2"
                  >
                    Manage profiles
                  </Link>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-start">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Profile</label>
                    <select
                      value={gcSelectedTcnProfileId}
                      onChange={(e) => setGcSelectedTcnProfileId(e.target.value)}
                      className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="">Default (.env)</option>
                      {(gcTcnProfiles || []).map(p => (
                        <option key={p.id} value={String(p.id)}>{p.name}</option>
                      ))}
                    </select>
                    {gcTcnProfiles.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1">No TCN profiles found in DB yet.</p>
                    )}
                  </div>
                  <div>
                    <div className="flex gap-2 sm:mt-5">
                      <button
                        type="button"
                        onClick={() => setGcTcnCreateOpen(true)}
                        className="h-10 px-4 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 text-sm font-medium"
                      >
                        New profile
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveTcnProfile(gcSelectedTcnProfileId)}
                        disabled={gcTcnSaving}
                        className="h-10 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
                      >
                        {gcTcnSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                        {gcTcnSaving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Profile management is handled in the dedicated admin page */}
              </div>

              {gcTcnCreateOpen && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                  onClick={() => !gcTcnCreateSaving && setGcTcnCreateOpen(false)}
                >
                  <div
                    className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">Create TCN Profile</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      This stores credentials in the database, with <strong>client secret encrypted</strong> using <code className="font-mono">TCN_SETTINGS_ENCRYPTION_KEY</code>.
                    </p>

                    <form onSubmit={handleCreateTcnProfile} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                          <input
                            value={gcTcnNew.name}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, name: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g. TCN Dev AU"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Customer Ref</label>
                          <input
                            value={gcTcnNew.customer_ref}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, customer_ref: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Token URL</label>
                          <input
                            value={gcTcnNew.token_url}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, token_url: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">API URL</label>
                          <input
                            value={gcTcnNew.api_url}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, api_url: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Client ID</label>
                          <input
                            value={gcTcnNew.client_id}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, client_id: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Client Secret</label>
                          <input
                            type="password"
                            value={gcTcnNew.client_secret}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, client_secret: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Promotion ID</label>
                          <input
                            value={gcTcnNew.promotion_id}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, promotion_id: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Product ID</label>
                          <input
                            value={gcTcnNew.product_id}
                            onChange={(e) => setGcTcnNew(prev => ({ ...prev, product_id: e.target.value }))}
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => setGcTcnCreateOpen(false)}
                          disabled={gcTcnCreateSaving}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={gcTcnCreateSaving}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {gcTcnCreateSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                          {gcTcnCreateSaving ? 'Creating…' : 'Create profile'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {gcTiers.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No tiers configured yet. Add your first tier below.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-blue-100">
                  <table className="min-w-full divide-y divide-blue-100">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Max Winners</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Issued</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                        <th className="px-4 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-blue-50">
                      {gcTiers.map((tier, i) => {
                        const issuedCount = Number(tier.issued_count) || 0;
                        const remaining = Number(tier.max_winners) - issuedCount;
                        const pct = Math.min(100, (issuedCount / Number(tier.max_winners)) * 100);
                        return (
                          <tr key={i} className="hover:bg-blue-50/40">
                            <td className="px-4 py-2 text-sm font-semibold text-gray-900">
                              ${Number(tier.amount).toFixed(2)}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-700">{tier.max_winners}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{issuedCount}</td>
                            <td className="px-4 py-2 text-sm">
                              <span className={remaining <= 0 ? 'text-red-600 font-medium' : 'text-green-700 font-medium'}>
                                {remaining <= 0 ? 'Exhausted' : remaining}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-2">
                              <button
                                type="button"
                                onClick={() => setTierToRemove(i)}
                                title="Remove tier"
                                disabled={gcTierDeletingId === tier.id}
                                className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
                <div className="bg-white border border-blue-100 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900">Add Gift Card Tier</h5>
                      <p className="text-xs text-gray-500 mt-1">Create amount and winner limits for TCN gift card rewards.</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1">
                      <Gift className="w-4 h-4" />
                      TCN reward rules
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-[170px_170px_auto] gap-3 items-end">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Amount ($)</label>
                      <input
                        type="number"
                        min="10"
                        max="1000"
                        step="0.01"
                        value={gcNewAmount}
                        onChange={(e) => setGcNewAmount(e.target.value)}
                        placeholder="e.g. 50"
                        className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-400 mt-1">Min $10 · Max $1,000</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Max Winners</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={gcNewMaxWinners}
                        onChange={(e) => setGcNewMaxWinners(e.target.value)}
                        placeholder="e.g. 100"
                        className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-400 mt-1">Entries eligible for this amount.</p>
                    </div>
                    <div className="sm:pb-[22px]">
                      <button
                        type="button"
                        onClick={handleAddTier}
                        disabled={!gcNewAmount || !gcNewMaxWinners || gcTierAdding}
                        className="h-10 w-full sm:w-auto px-4 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-1"
                      >
                        {gcTierAdding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {gcTierAdding ? 'Adding…' : 'Add tier'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-blue-100 rounded-lg p-4 space-y-3">
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900">Reward Rule</h5>
                    <p className="text-xs text-gray-500 mt-1">
                      {isWin1OfXMechanic
                        ? 'Control how often an eligible entry receives a gift card.'
                        : 'This campaign uses the standard gift card reward flow.'}
                    </p>
                  </div>

                  {isWin1OfXMechanic ? (
                    <>
                      <label className="block text-xs font-medium text-gray-600">Win 1 of X</label>
                      <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Win 1 of</span>
                          <input
                            type="number"
                            min="2"
                            step="1"
                            value={gcWin1X}
                            onChange={(e) => setGcWin1X(e.target.value)}
                            placeholder="e.g. 3"
                            className="h-10 w-full px-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Allowed values: any whole number starting from 2</p>
                      </div>
                    </>
                  ) : (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                      <p className="text-sm text-gray-600">Every eligible entry can receive a gift card until the configured tier limits are exhausted.</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSaveGiftCardRule}
                    disabled={gcRuleSaving || (isWin1OfXMechanic && !gcWin1X.trim())}
                    className="h-10 w-full px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
                  >
                    {gcRuleSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                    {gcRuleSaving ? 'Saving…' : 'Save rule'}
                  </button>
                </div>
              </div>

              {gcTiers.length > 1 && (
                <p className="text-xs text-gray-500">
                  When multiple tiers have remaining winners, one will be <strong>randomly selected</strong> for each eligible entry.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {enabled && (
        <>
          <p className="text-sm text-gray-600">
            Upload or add voucher codes for this cashback campaign. Participants can receive one voucher per entry when mechanics type is Cashback.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-sm font-medium text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm font-medium text-green-700">Available</p>
          <p className="text-2xl font-bold text-green-800">{stats.available}</p>
        </div>
        <div className="bg-gray-100 border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-medium text-gray-600">Used</p>
          <p className="text-2xl font-bold text-gray-800">{stats.used}</p>
        </div>
      </div>

      {/* Upload CSV */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
          <CloudUpload className="w-5 h-5" />
          Upload voucher list (CSV)
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          CSV: one column for voucher code, optional second column for amount. Header row with &quot;voucher_code&quot; and/or &quot;voucher_amount&quot; is supported.
        </p>
        <form onSubmit={handleUploadCsv} className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-teal-100 file:text-teal-800 file:font-medium"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Replace existing vouchers
            </label>
            <button
              type="submit"
              disabled={!csvFile || uploading}
              className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {uploading ? 'Uploading…' : 'Upload CSV'}
            </button>
          </div>
        </form>
      </div>

      {/* Add single voucher */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <h4 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Add single voucher
        </h4>
        <form onSubmit={handleAddSingle} className="flex flex-wrap gap-3">
          <input
            type="text"
            value={singleCode}
            onChange={(e) => setSingleCode(e.target.value)}
            placeholder="Voucher code"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent w-64"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={singleAmount}
            onChange={(e) => setSingleAmount(e.target.value)}
            placeholder="Amount (optional)"
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent w-32"
          />
          <button
            type="submit"
            disabled={!singleCode.trim() || addingSingle}
            className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
          >
            {addingSingle ? 'Adding…' : 'Add'}
          </button>
        </form>
      </div>

      {/* Voucher list */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-white px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <h4 className="font-medium text-gray-900">Voucher list</h4>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder="Search code…"
              className="h-9 px-3 border border-gray-300 rounded-lg text-sm w-48 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-9 px-3 border border-gray-300 rounded-lg text-sm min-w-[120px]"
            >
              <option value="">All statuses</option>
              <option value="available">Available</option>
              <option value="used">Used</option>
            </select>
            <button
              type="button"
              onClick={applySearch}
              className="h-9 px-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
            >
              Search
            </button>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={handleDeleteSelected}
                disabled={deleting}
                className="h-9 px-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm flex items-center gap-1 font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Delete ({selectedIds.size})
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : vouchers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No vouchers yet. Upload a CSV or add a single voucher above.</div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={vouchers.length > 0 && vouchers.every(v => selectedIds.has(v.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(new Set(vouchers.map(v => v.id)));
                          } else {
                            setSelectedIds(new Set());
                          }
                        }}
                        className="rounded border-gray-300 text-teal-600"
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Used at</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vouchers.map((v) => (
                    <tr key={v.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(v.id)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(v.id);
                            else next.delete(v.id);
                            setSelectedIds(next);
                          }}
                          className="rounded border-gray-300 text-teal-600"
                        />
                      </td>
                      <td className="px-4 py-2 font-mono text-sm">{v.voucher_code}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{v.voucher_amount != null ? Number(v.voucher_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${v.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {v.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">{formatDate(v.used_at)}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{formatDate(v.created_at)}</td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => openEditVoucher(v)}
                          className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Edit voucher"
                        >
                          <SquarePen className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                  <span>
                    Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={pagination.page <= 1}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
        </>
      )}

      {/* Remove tier confirmation */}
      {tierToRemove !== null && gcTiers[tierToRemove] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setTierToRemove(null)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Remove tier?</h4>
            <p className="text-sm text-gray-600 mb-4">
              This will remove the tier for <strong>${Number(gcTiers[tierToRemove].amount).toFixed(2)}</strong> (max {gcTiers[tierToRemove].max_winners} winners). You can add it again later. Remember to save after making changes.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setTierToRemove(null)}
                disabled={gcTierDeletingId === gcTiers[tierToRemove]?.id}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleRemoveTier(tierToRemove)}
                disabled={gcTierDeletingId === gcTiers[tierToRemove]?.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4" />
                {gcTierDeletingId === gcTiers[tierToRemove]?.id ? 'Removing…' : 'Remove tier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit voucher modal */}
      {editingVoucher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeEditVoucher}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Edit voucher</h4>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={editCode}
                  onChange={(e) => setEditCode(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (optional)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder="Leave empty for no amount"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeEditVoucher}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit || !editCode.trim()}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingEdit ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashbackConfig;
